import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatFormField, MatHint, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import type { InterruptItem } from "../../../model/interrupt.types";
import { hitlIssueCodes } from "../../../model/interrupt.types";
import { HitlAskContextComponent } from "../hitl-ask-context/hitl-ask-context.component";
import { NotificationCardComponent } from "../../shared/notification-card/notification-card.component";

type CardState = "pending" | "resolved" | "expired";

const TIMEOUT_MS = 5 * 60 * 1000;

/**
 * HITL card for kind === 'text' | 'date' | 'number' | 'geocoding' | 'geometry'.
 * Renders a typed form field based on kind. Emits the raw string value.
 */
@Component({
  selector: "app-hitl-form-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButton,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    HitlAskContextComponent,
    NotificationCardComponent,
  ],
  templateUrl: "./hitl-form-card.component.html",
  styleUrl: "./hitl-form-card.component.scss",
})
export class HitlFormCardComponent implements OnInit {
  entry = input.required<InterruptItem>();

  readonly submitted = output<string>();

  protected readonly state = signal<CardState>("pending");

  protected readonly value = signal("");

  protected readonly resolvedValue = signal("");

  protected readonly canSubmit = computed(() => this.value().trim().length > 0);

  protected readonly inputType = computed((): string => {
    switch (this.entry().kind) {
      case "date":
        return "date";
      case "number":
        return "number";
      default:
        return "text";
    }
  });

  protected readonly locationHint = computed((): string | null => {
    switch (this.entry().kind) {
      case "geocoding":
        return "Enter a place name or address (e.g. 'Denver, CO')";
      case "geometry":
        return "Enter WKT, GeoJSON, lat/lng, or bbox";
      default:
        return null;
    }
  });

  protected readonly isMultiline = computed(() => this.entry().kind === "text");

  /**
   * Cancel-as-refusal offer (HITL contract §4): only for engine-stamped asks
   * (issue_codes / plan_surfaces). This card renders the free-form kinds only
   * (text/date/number/geometry/geocoding), so no kind check is needed here;
   * select/multiselect surface the engine's `__none__` option and boolean's
   * "no" is already the refusal.
   */
  protected readonly canCancel = computed(
    () => hitlIssueCodes(this.entry()).length > 0 || "plan_surfaces" in (this.entry().metadata ?? {}),
  );

  readonly cancelled = output<void>();

  readonly #destroyRef = inject(DestroyRef);

  private timeoutHandle?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.timeoutHandle = setTimeout(() => this.expire(), TIMEOUT_MS);
    this.#destroyRef.onDestroy(() => clearTimeout(this.timeoutHandle));
  }

  protected submit(): void {
    if (!this.canSubmit()) return;
    clearTimeout(this.timeoutHandle);
    const trimmed = this.value().trim();
    this.resolvedValue.set(trimmed);
    this.state.set("resolved");
    this.submitted.emit(trimmed);
  }

  protected handleEnter(event: KeyboardEvent): void {
    if (!event.shiftKey && !this.isMultiline()) {
      event.preventDefault();
      this.submit();
    }
  }

  /** Emits the refusal offer; the host resumes with the single-entry `__none__` payload. */
  protected cancel(): void {
    if (!this.canCancel()) return;
    clearTimeout(this.timeoutHandle);
    this.cancelled.emit();
  }

  protected restart(): void {
    clearTimeout(this.timeoutHandle);
    this.value.set("");
    this.state.set("pending");
    this.timeoutHandle = setTimeout(() => this.expire(), TIMEOUT_MS);
  }

  private expire(): void {
    this.state.set("expired");
  }
}
